<script setup lang="ts">
import type { GameDepositStep } from '@/composables/useGameDepositModal';

const { current } = defineProps<{ current: GameDepositStep }>();

// Regra do stepper:
// - azul quando está na etapa (ativo) ou já passou por ela (concluído ✓)
// - cinza quando ainda não chegou
const steps = [
  { id: 1, label: 'Escolha o valor' },
  { id: 2, label: 'Pagamento' },
  { id: 3, label: 'Confirmação' },
] as const;

const isReached = (id: number) => current >= id;
const isDone = (id: number) => current > id;
</script>

<template>
  <div class="gdm-stepper">
    <div class="gdm-track">
      <template
        v-for="(s, index) in steps"
        :key="s.id"
      >
        <span
          v-if="index > 0"
          class="gdm-bar"
          :class="{ 'is-blue': isReached(s.id) }"
        />

        <div class="gdm-node">
          <span
            class="gdm-dot"
            :class="{ 'is-blue': isReached(s.id) }"
          >
            <NuxtIcon
              v-if="isDone(s.id)"
              name="check"
            />
            <template v-else>{{ s.id }}</template>
          </span>
          <span
            class="gdm-label"
            :class="{ 'is-blue': isReached(s.id) }"
          >
            {{ s.label }}
          </span>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
$accent: #0485f7;
$muted: rgba(255, 255, 255, 0.2);

.gdm-stepper {
  width: 100%;
  padding: 12px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.02);
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-track {
  display: flex;
  align-items: flex-start;
  justify-content: center;
}

.gdm-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.gdm-dot {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid $muted;
  color: $muted;
  font-size: 9px;
  font-weight: 700;
  line-height: 1;

  :deep(svg) {
    width: 9px;
    height: 9px;
  }

  // Atingido (ativo ou concluído): azul preenchido, texto/ícone branco.
  &.is-blue {
    background: $accent;
    border-color: transparent;
    color: #fff;
  }
}

.gdm-bar {
  flex: 1;
  max-width: 100px;
  height: 1px;
  margin-top: 8px;
  background: $muted;

  &.is-blue {
    background: $accent;
  }
}

.gdm-label {
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  color: $muted;

  &.is-blue {
    color: $accent;
  }
}
</style>
